import { styled } from '@mui/material';
import React from 'react';
import { VariantComponentProps } from './variant';
import Adornment from './Adornment';
import { Input as BaseInput } from './Input';

const Input = styled(BaseInput)(() => ({
    '&:disabled': {
        cursor: 'not-allowed !important',
    },
}));

const Base = styled('div', {
    shouldForwardProp: prop =>
        prop !== 'disabled' &&
        prop !== 'error' &&
        prop !== 'height' &&
        prop !== 'backgroundColor' &&
        prop !== 'sx',
})<{
    disabled?: boolean;
    error?: boolean;
    height: number;
    backgroundColor?: string;
}>(({ theme, disabled, error, height, backgroundColor }) => ({
    'position': 'relative',
    'width': '100%',
    'display': 'flex',
    'justifyContent': 'flex-start',
    'alignItems': 'center',
    'borderRadius': theme.shape.borderRadius,
    'backgroundColor':
        backgroundColor || theme.palette.background.backgroundColorDark,
    'boxShadow': '0px 0px 5px rgba(0, 0, 0, 0.1)',
    'overflow': 'hidden',
    'height': height,

    ...(disabled && {
        backgroundColor: theme.palette.background.backgroundColor,
    }),

    ...(error && {}),

    '& > input:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 1000px ${backgroundColor || theme.palette.background.backgroundColorDark} inset`,
    },
    '& > input:-webkit-autofill:hover': {
        WebkitBoxShadow: `0 0 0 1000px ${backgroundColor || theme.palette.background.backgroundColorDark} inset`,
    },
    '& > input:-webkit-autofill:focus': {
        WebkitBoxShadow: `0 0 0 1000px ${backgroundColor || theme.palette.background.backgroundColorDark} inset`,
    },
    '& > input:-webkit-autofill:active': {
        WebkitBoxShadow: `0 0 0 1000px ${backgroundColor || theme.palette.background.backgroundColorDark} inset`,
    },
}));

const FilledInput = React.forwardRef<HTMLInputElement, VariantComponentProps>(
    (
        {
            // Core behavior props
            height = 40,
            error,
            disabled,

            // Styling props
            backgroundColor,
            sx,
            inputSx,

            // Event handlers for <Base>
            onBaseClick,
            onBaseMouseEnter,
            onBaseMouseLeave,

            // UI elements inside the input
            startAdornment,
            startAdornmentRef,
            startAdornmentWidth,
            startAdornmentSx,
            endAdornment,
            endAdornmentRef,
            endAdornmentWidth,
            endAdornmentSx,

            // Other input-related props
            ...props
        },
        externalRef
    ) => {
        const inputRef = React.useRef<HTMLInputElement | null>(null);

        // If externalRef is provided, pass it down to allow forwarding
        React.useImperativeHandle(externalRef, () => inputRef.current!);

        // Handle clicking on <Base> to focus the input
        const handleBaseClick = (e: React.MouseEvent<HTMLDivElement>) => {
            inputRef.current?.focus(); // Manually focus input
            onBaseClick?.(e); // Call user-defined handler if provided
        };

        return (
            <Base
                disabled={disabled}
                error={error}
                height={height}
                backgroundColor={backgroundColor}
                sx={sx}
                onClick={handleBaseClick}
                onMouseEnter={onBaseMouseEnter}
                onMouseLeave={onBaseMouseLeave}>
                <Input
                    {...props}
                    ref={inputRef}
                    disabled={disabled}
                    startAdornmentWidth={startAdornmentWidth}
                    endAdornmentWidth={endAdornmentWidth}
                    sx={inputSx}
                />
                {startAdornment && (
                    <Adornment
                        ref={startAdornmentRef}
                        position='start'
                        sx={startAdornmentSx}>
                        {startAdornment}
                    </Adornment>
                )}
                {endAdornment && (
                    <Adornment
                        ref={endAdornmentRef}
                        position='end'
                        sx={endAdornmentSx}>
                        {endAdornment}
                    </Adornment>
                )}
            </Base>
        );
    }
);
FilledInput.displayName = 'FilledInput';

export default FilledInput;
